import React, { useEffect, useState } from "react";
import { TfiAngleDown, TfiAngleUp } from "react-icons/tfi";
import MenuImage from "../../../helper/images/menuimage.png";
import VegIcon from "../../../helper/svg/Veg.svg";
import NonVegIcon from "../../../helper/svg/NonVeg.svg";
import { MdStar } from "react-icons/md";
import { FaShoppingCart } from "react-icons/fa";
import { HiArrowRight } from "react-icons/hi";
import {
  getAllCategories,
  getRestaurantMenu,
} from "../../../api/user/restaurants";
import {
  getCart,
  addOrUpdateCartItem,
  removeCartItem,
} from "../../../api/user/restaurants/cart";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Pagination from "../../../components/common/Pagination";
import toast from "react-hot-toast";
import { useTranslation } from "../../../hooks/useTranslation";
import { useNavigate } from "react-router-dom";

const AccordionSection = ({ title, children, index, openIndex, setOpenIndex }) => {
  const isOpen = openIndex === index;

  return (
    <div className="py-[25px] border-b border-[#DDDDDD]">
      <div
        className="flex justify-between text-xl font-semibold cursor-pointer"
        onClick={() => setOpenIndex(isOpen ? null : index)}
      >
        {title}
        <span>{isOpen ? <TfiAngleUp size={20} /> : <TfiAngleDown size={20} />}</span>
      </div>
      {isOpen && <div>{children}</div>}
    </div>
  );
};

const Menu = ({ restaurantId, reservationId }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [page, setPage] = useState(1);
  const limit = 5;
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [cartItems, setCartItems] = useState([]);
  const [countMap, setCountMap] = useState({});
  const [notesMap, setNotesMap] = useState({});
  const [showReplaceCartModal, setShowReplaceCartModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // 🔹 Categories
  const { data: categoryData } = useQuery({
    queryKey: ["restaurant-menu-categories", restaurantId],
    queryFn: () => getAllCategories(restaurantId),
    enabled: !!restaurantId,
  });

  const { data: menuData } = useQuery({
    queryKey: ["restaurant-menu", restaurantId, openIndex, page, limit],
    queryFn: () => getRestaurantMenu(openIndex, page, limit),
    enabled: !!openIndex,
    keepPreviousData: true,
  });


  const categories = categoryData?.data?.categories || [];
  const restaurantMenu = menuData?.data?.items || [];
  const pagination = menuData?.data?.pagination || {};

  // 🔹 Cart
  const { data: cartData } = useQuery({
    queryKey: ["cart"],
    queryFn: getCart,
  });

  // Sync cart state
  useEffect(() => {
    // Only sync cart items if they belong to the current restaurant
    if (cartData?.data?.items?.length > 0 && cartData?.data?.restaurantId) {
      const cartRestaurantId = String(cartData.data.restaurantId);
      const currentRestaurantId = String(restaurantId);
      
      // Only show cart items if they belong to the current restaurant
      if (cartRestaurantId === currentRestaurantId) {
        const formatted = cartData.data.items.map((item) => ({
          ...item.itemId,
          quantity: item.quantity,
          note: item.note || "",
          cartItemId: item._id,
        }));

        setCartItems(formatted);

        const counts = {};
        const notes = {};
        formatted.forEach((item) => {
          counts[item._id] = item.quantity;
          notes[item._id] = item.note;
        });
        setCountMap(counts);
        setNotesMap(notes);
      } else {
        // Cart belongs to a different restaurant, clear the local state
        setCartItems([]);
        setCountMap({});
        setNotesMap({});
      }
    } else {
      setCartItems([]);
      setCountMap({});
      setNotesMap({});
    }
  }, [cartData, restaurantId]);

  // 🔹 Mutations
  const addOrUpdateMutation = useMutation({
    mutationFn: ({ itemId }) => addOrUpdateCartItem({ restaurantId, itemId }),
    onSuccess: () => {
      queryClient.invalidateQueries(["cart"]);
      toast.success(t("restaurantmenu").addedItemsToCart?.replace("{{count}}", "1") || "Item added to cart");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("restaurantmenu").failedAddItem || "Failed to add item");
    },
  });

  const removeMutation = useMutation({
    mutationFn: ({ itemId }) => removeCartItem({ restaurantId, itemId }),
    onSuccess: () => {
      queryClient.invalidateQueries(["cart"]);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to remove item");
    },
  });

  const handleAddToCart = (itemId) => {
    // Check if cart has items from a different restaurant
    const cartRestaurantId = cartData?.data?.restaurantId ? String(cartData.data.restaurantId) : null;
    const currentRestaurantId = restaurantId ? String(restaurantId) : null;
    const hasDifferentRestaurant = cartRestaurantId && cartRestaurantId !== currentRestaurantId && cartData?.data?.items?.length > 0;

    if (hasDifferentRestaurant) {
      // Show confirmation modal
      setPendingAction(() => () => {
        addOrUpdateMutation.mutate({ restaurantId, itemId });
      });
      setShowReplaceCartModal(true);
      return;
    }

    addOrUpdateMutation.mutate({ restaurantId, itemId });
  };

  const handleConfirmReplaceCart = () => {
    setShowReplaceCartModal(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const handleCancelReplaceCart = () => {
    setShowReplaceCartModal(false);
    setPendingAction(null);
  };


  const handleRemoveFromCart = (itemId) => {
    removeMutation.mutate({ restaurantId, itemId });
  };

  const isItemInCart = (itemId) => {
    return cartItems.some((item) => item._id === itemId);
  };

  const totalItemsInCart = cartItems.reduce((sum, item) => sum + (countMap[item._id] || 0), 0);
  // Only show checkout button if cart items belong to current restaurant
  const cartRestaurantId = cartData?.data?.restaurantId ? String(cartData.data.restaurantId) : null;
  const currentRestaurantId = restaurantId ? String(restaurantId) : null;
  const hasItemsInCart = cartItems.length > 0 && cartRestaurantId === currentRestaurantId;

  const handleViewCart = () => {
    navigate("/cart", {
      state: {
        restaurantId,
        reservationId,
      },
    });
  };

  return (
    <>
      {/* Replace Cart Confirmation Modal */}
      {showReplaceCartModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[20px] w-full max-w-md p-6 md:p-8 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold text-black">
                {t("restaurantmenu").replaceCartTitle || "Replace Cart Items?"}
              </h3>
              <button
                onClick={handleCancelReplaceCart}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="mb-6">
              <p className="text-base text-gray-700 mb-4">
                {t("restaurantmenu").replaceCartMessage || "You have items from another restaurant in your cart. Adding items from this restaurant will replace all existing items in your cart."}
              </p>
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800">
                  <strong>{t("restaurantmenu").replaceCartWarning || "Warning:"}</strong> {t("restaurantmenu").replaceCartWarningText || "This action cannot be undone."}
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCancelReplaceCart}
                className="flex-1 py-3 px-4 rounded-[10px] bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-colors"
              >
                {t("restaurantmenu").cancel || "Cancel"}
              </button>
              <button
                onClick={handleConfirmReplaceCart}
                className="flex-1 py-3 px-4 rounded-[10px] bg-[#4CAF50] text-white font-semibold hover:bg-[#209b25] transition-colors"
              >
                {t("restaurantmenu").replaceCart || "Replace Cart"}
              </button>
            </div>
          </div>
        </div>
      )}

      {hasItemsInCart && (
        <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-10 p-4 mb-4 rounded-t-lg">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <FaShoppingCart className="text-green-600 text-xl" />
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {totalItemsInCart} {totalItemsInCart === 1 ? (t("restaurantmenu")?.item || "item") : (t("restaurantmenu")?.items || "items")} {t("restaurantmenu")?.inCart || "in cart"}
                </p>
                <p className="text-xs text-gray-500">{t("restaurantmenu")?.clickToViewCart || "Click to view cart"}</p>
              </div>
            </div>
            <button
              onClick={handleViewCart}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-full font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <span>{t("restaurant")?.checkout || t("restaurantmenu")?.viewCart || "View Cart"}</span>
              <HiArrowRight className="text-lg" />
            </button>
          </div>
        </div>
      )}
      {categories.length > 0 ? (
        categories.map((cat) => (
          <AccordionSection
            key={cat._id}
            index={cat._id}
            openIndex={openIndex}
            setOpenIndex={setOpenIndex}
            title={`${cat.category} (${cat.totalMenuItems})`}
          >
            {restaurantMenu?.length > 0 ? (
              restaurantMenu.map((item) => (
                <div
                  key={item._id}
                  className="grid lg:grid-cols-[1fr_auto] gap-6 bg-white border border-gray-200 rounded-[5px] p-4 mt-5 shadow-sm"
                >
                  {/* Image + info */}
                  <div className="flex gap-6">
                    <img
                      src={item?.images?.[0] || MenuImage}
                      className="w-24 h-24 object-cover rounded"
                      alt="Menu"
                    />
                    <div>
                      {item?.foodType === "veg" ? (
                        <img src={VegIcon} alt="Veg Icon" className="w-5 h-5" />
                      ) : (
                        <img src={NonVegIcon} alt="Non-Veg Icon" className="w-5 h-5" />
                      )}
                      <h3 className="font-semibold text-lg mt-1">{item?.itemName}</h3>
                      <p className="text-gray-600 text-sm mt-1">{item?.description}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="font-bold text-gray-900">₾{item?.price}</span>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <MdStar key={i} size={18} className="text-yellow-500" />
                          ))}
                          <span className="text-green-600 text-sm">4.3 (7)</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cart actions */}


                  
                    <div>
                      <div className="flex justify-between border rounded-full px-4 py-2">
                        <button
                          onClick={() => handleRemoveFromCart(item._id)}
                          className="text-lg px-2"
                        >
                          –
                        </button>
                        <span>{countMap[item._id] || 0}</span>
                        <button
                          onClick={() => handleAddToCart(item._id)}
                          className="text-lg px-2"
                        >
                          +
                        </button>
                      </div>

                      <button
                        onClick={() => handleAddToCart(item._id)}
                        className="mt-3 w-full bg-green-600 text-white rounded py-2 font-semibold hover:bg-green-700"
                      >
                        {isItemInCart(item._id) ? t("restaurantmenu").added || "Added" : t("restaurantmenu").addToCart || "Add to Cart"}
                      </button>

                      {/* <textarea
                      className="w-full mt-3 border rounded p-2 text-sm"
                      placeholder='e.g., "No onion", "Extra cheese"'
                      value={notesMap[item._id] || ""}
                      onChange={(e) => handleNotesChange(item._id, e.target.value)}
                    ></textarea> */}
                    </div>
                  

                </div>
              ))
            ) : (
              <p className="text-gray-500 mt-4">No items in this category</p>
            )}

            {pagination.totalPages > 1 && (
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                setPage={setPage}
              />
            )}
          </AccordionSection>
        ))
      ) : (
        <div className="flex justify-center mt-10 text-gray-600 font-medium">
          No Menu Items Found
        </div>
      )}
    </>
  );
};

export default Menu;
